import type { Category } from '../types';

/**
 * "Your gear" — GL Plus's own operational guidance (work half). No brands, no
 * prices: describe what to look for. Deductibility is a pointer to the
 * Deductions category, mentioned at most once per guide.
 */
export const gear: Category = {
  slug: "your-gear",
  half: "work",
  accent: '#343F5B',
  icon: "flag",
  label: { en: "Your gear", es: "Tu equipo" },
  blurb: {
    en: "Coolers and bags that work, phone mount, a cart for apartments, winter gear. Most of it is deductible.",
    es: "Hieleras y bolsas que funcionan, soporte para el teléfono, un carrito para departamentos, equipo de invierno. La mayoría es deducible.",
  },
  guides: [
    {
      slug: "coolers-and-bags",
      related: [
        "deductions/bags-coolers-gear",
        "food-safety/cold-chain-basics",
        "food-safety/raw-meat-separation",
        "safety-on-deliveries/lifting-and-carrying",
      ],
      body: {
        en: {
          title: "Coolers and bags that work",
          summary:
            "What to look for in a cooler and insulated bags, the two pieces of gear that protect your customers' food.",
          sections: [
            {
              heading: "The cooler",
              bullets: [
                "Hard-sided, with a lid that seals. A soft cooler is fine as a second.",
                "Big enough for a full frozen order, small enough to carry when it is loaded.",
                "A drain plug, so you can rinse it out after a raw-meat run.",
                "A light color. It reflects heat in a parked vehicle.",
                "Handles that will not snap under a full load.",
              ],
            },
            {
              heading: "Insulated bags",
              paragraphs: [
                "Two or three zip-top insulated bags that fold flat. Look for a wipeable liner, a zipper rather than a drawstring, and enough height for a milk jug standing up.",
                "For dry goods, reusable grocery bags with a stiff bottom are easier to carry and do not tip in the vehicle.",
              ],
            },
            {
              heading: "Ice packs",
              paragraphs: [
                "Flat, reusable packs freeze faster and pack tighter than bulky ones. Keep two sets: one in the freezer, one in the cooler. Gel packs stay cold longer than frozen water bottles, though a frozen bottle works in a pinch.",
              ],
            },
            {
              heading: "Keeping it clean",
              paragraphs: [
                "Choose gear you can wipe out. Cloth seams hold spills and smells. Rinse coolers after raw-meat runs and leave them open to dry.",
                "Most of this is deductible. See Deductions for what to keep.",
              ],
            },
          ],
          actions: [
            "Measure your cargo space, then pick a cooler that fits with the lid closed.",
            "Buy or freeze a second set of ice packs.",
            "Save the receipts for anything you buy this week.",
          ],
        },
        es: {
          title: "Hieleras y bolsas que funcionan",
          summary:
            "Qué buscar en una hielera y en las bolsas térmicas, las dos piezas de equipo que protegen los alimentos de tus clientes.",
          sections: [
            {
              heading: "La hielera",
              bullets: [
                "Rígida, con una tapa que cierre bien. Una hielera blanda sirve como segunda opción.",
                "Lo bastante grande para un pedido completo de congelados, y lo bastante pequeña para cargarla llena.",
                "Con tapón de drenaje, para enjuagarla después de un recorrido con carne cruda.",
                "De color claro. Refleja el calor en un vehículo estacionado.",
                "Con asas que no se rompan con la carga completa.",
              ],
            },
            {
              heading: "Bolsas térmicas",
              paragraphs: [
                "Dos o tres bolsas térmicas con cierre que se doblen planas. Busca un forro que se pueda limpiar, cierre en lugar de cordón, y altura suficiente para un envase de leche parado.",
                "Para los productos secos, las bolsas reutilizables con fondo rígido son más fáciles de cargar y no se vuelcan en el vehículo.",
              ],
            },
            {
              heading: "Paquetes de hielo",
              paragraphs: [
                "Los paquetes planos y reutilizables se congelan más rápido y ocupan menos espacio que los voluminosos. Ten dos juegos: uno en el congelador y otro en la hielera. Los paquetes de gel se mantienen fríos más tiempo que las botellas de agua congeladas, aunque una botella congelada sirve en un apuro.",
              ],
            },
            {
              heading: "Mantenerlo limpio",
              paragraphs: [
                "Elige equipo que puedas limpiar con un paño. Las costuras de tela retienen derrames y olores. Enjuaga las hieleras después de recorridos con carne cruda y déjalas abiertas para que se sequen.",
                "La mayoría de esto es deducible. Consulta Deducciones para saber qué guardar.",
              ],
            },
          ],
          actions: [
            "Mide tu espacio de carga y luego elige una hielera que quepa con la tapa cerrada.",
            "Compra o congela un segundo juego de paquetes de hielo.",
            "Guarda los recibos de todo lo que compres esta semana.",
          ],
        },
      },
    },
    {
      slug: "phone-mount-and-power",
      related: [
        "deductions/phone-and-data",
        "safety-on-deliveries/parking-and-walking-routes",
        "safety-on-deliveries/night-deliveries",
      ],
      body: {
        en: {
          title: "Phone mount and power",
          summary:
            "Your phone runs the app, the map, and the proof of delivery. Mount it where you can see it and keep it charged.",
          sections: [
            {
              heading: "The mount",
              bullets: [
                "Holds the phone at eye level without blocking your view of the road. Dash or vent, not the middle of the windshield.",
                "One-handed in and out. You will do it thirty times a day.",
                "A grip firm enough to survive potholes.",
                "Check your local rules on where a phone may be mounted.",
              ],
            },
            {
              heading: "Power",
              paragraphs: [
                "A run is hours of navigation, camera, and screen-on time. Keep a charging cable in the vehicle and a small power bank in your bag for walk-up deliveries and apartment buildings. Look for a bank that can charge your phone fully at least once.",
              ],
            },
            {
              heading: "Data and coverage",
              paragraphs: [
                "The Runner app needs a signal to update stops and upload photos. Check your plan's data allowance; navigation and photo uploads add up. If some parts of your area have weak coverage, download offline maps for those zones ahead of time.",
              ],
            },
            {
              heading: "Care for the phone",
              paragraphs: [
                "Heat is the enemy. A phone left on a hot dash can shut down mid-run. Take it with you at every stop, keep it out of direct sun, and use a plain case that does not trap heat.",
                "The mount, cables, and the work share of your phone plan are mostly deductible. See Deductions.",
              ],
            },
          ],
          actions: [
            "Mount your phone and drive around the block to check that you can see it without leaning.",
            "Put a spare charging cable and a power bank in your delivery bag.",
            "Download offline maps for your delivery area.",
          ],
        },
        es: {
          title: "Soporte y carga para el teléfono",
          summary:
            "Tu teléfono corre la app, el mapa y la prueba de entrega. Móntalo donde lo veas y mantenlo cargado.",
          sections: [
            {
              heading: "El soporte",
              bullets: [
                "Sostiene el teléfono a la altura de los ojos sin bloquear tu vista del camino. En el tablero o en la rejilla, no en el centro del parabrisas.",
                "Se pone y se quita con una mano. Lo harás treinta veces al día.",
                "Con un agarre lo bastante firme para aguantar los baches.",
                "Revisa las reglas locales sobre dónde se puede montar un teléfono.",
              ],
            },
            {
              heading: "Energía",
              paragraphs: [
                "Un recorrido son horas de navegación, cámara y pantalla encendida. Ten un cable de carga en el vehículo y una batería portátil pequeña en tu bolsa para las entregas a pie y los edificios de departamentos. Busca una batería que pueda cargar tu teléfono por completo al menos una vez.",
              ],
            },
            {
              heading: "Datos y cobertura",
              paragraphs: [
                "La app de Runner necesita señal para actualizar las paradas y subir fotos. Revisa el límite de datos de tu plan; la navegación y la subida de fotos suman. Si algunas zonas de tu área tienen mala cobertura, descarga mapas sin conexión de esas zonas con anticipación.",
              ],
            },
            {
              heading: "Cuida el teléfono",
              paragraphs: [
                "El calor es el enemigo. Un teléfono dejado en un tablero caliente puede apagarse a mitad del recorrido. Llévalo contigo en cada parada, mantenlo fuera del sol directo y usa una funda sencilla que no retenga el calor.",
                "El soporte, los cables y la parte de trabajo de tu plan de teléfono son en su mayoría deducibles. Consulta Deducciones.",
              ],
            },
          ],
          actions: [
            "Monta tu teléfono y da una vuelta a la manzana para confirmar que lo ves sin inclinarte.",
            "Pon un cable de carga de repuesto y una batería portátil en tu bolsa de entregas.",
            "Descarga mapas sin conexión de tu área de entregas.",
          ],
        },
      },
    },
    {
      slug: "cart-and-winter-gear",
      related: [
        "deductions/bags-coolers-gear",
        "safety-on-deliveries/lifting-and-carrying",
        "safety-on-deliveries/severe-weather",
        "safety-on-deliveries/night-deliveries",
      ],
      body: {
        en: {
          title: "A cart for apartments, and winter gear",
          summary:
            "A folding cart saves your back on apartment runs, and the right winter gear keeps you and the groceries safe when it is cold and slick.",
          sections: [
            {
              heading: "The folding cart",
              bullets: [
                "Folds flat to fit your cargo area.",
                "Big wheels that handle curbs, gravel, and the edge of a stair.",
                "Rated for a full order plus a cooler. Check the weight limit.",
                "A frame you can lift into the vehicle with one hand.",
                "Stair-climbing wheels if your area is mostly walk-ups.",
              ],
            },
            {
              heading: "Winter gear for you",
              paragraphs: [
                "Insulated, waterproof boots with real tread. Gloves you can use a phone in. Layers you can shed between the vehicle and the door. A headlamp or clip-on light for dark doorsteps.",
              ],
            },
            {
              heading: "Winter gear for the groceries",
              paragraphs: [
                "Cold weather does not mean cold food is safe. The heater warms the cabin, so keep cold items in the cooler even in January. Freezing hurts produce and eggs, too. Keep those in an insulated bag off the cold floor.",
              ],
            },
            {
              heading: "Winter gear for the vehicle",
              bullets: [
                "A scraper, a small shovel, and a bag of sand or cat litter for traction.",
                "A blanket and water, in case you get stuck.",
                "Traction aids if your area gets ice.",
              ],
              paragraphs: ["Most of this is deductible. See Deductions."],
            },
          ],
          actions: [
            "If you deliver to apartments, borrow or buy a cart and try one run with it.",
            "Put a winter kit in the vehicle before the first cold snap.",
            "Test your gloves with the app. You will need to tap, swipe, and take photos.",
          ],
        },
        es: {
          title: "Un carrito para departamentos, y equipo de invierno",
          summary:
            "Un carrito plegable te cuida la espalda en los recorridos por departamentos, y el equipo de invierno adecuado te protege a ti y a las compras cuando hace frío y el piso está resbaloso.",
          sections: [
            {
              heading: "El carrito plegable",
              bullets: [
                "Se dobla plano para caber en tu área de carga.",
                "Ruedas grandes que aguantan banquetas, grava y el borde de un escalón.",
                "Con capacidad para un pedido completo más una hielera. Revisa el límite de peso.",
                "Un armazón que puedas subir al vehículo con una mano.",
                "Ruedas para escaleras si en tu área predominan los edificios sin elevador.",
              ],
            },
            {
              heading: "Equipo de invierno para ti",
              paragraphs: [
                "Botas térmicas e impermeables con buena suela. Guantes con los que puedas usar el teléfono. Capas que puedas quitarte entre el vehículo y la puerta. Una linterna frontal o de clip para las entradas oscuras.",
              ],
            },
            {
              heading: "Equipo de invierno para las compras",
              paragraphs: [
                "Que haga frío no significa que los alimentos fríos estén a salvo. La calefacción calienta la cabina, así que mantén los artículos fríos en la hielera incluso en enero. La congelación también daña las frutas, las verduras y los huevos. Llévalos en una bolsa térmica, lejos del piso frío.",
              ],
            },
            {
              heading: "Equipo de invierno para el vehículo",
              bullets: [
                "Un raspador, una pala pequeña y una bolsa de arena o arena para gatos para ganar tracción.",
                "Una cobija y agua, por si te quedas atascado.",
                "Cadenas u otros auxiliares de tracción si en tu área se forma hielo.",
              ],
              paragraphs: ["La mayoría de esto es deducible. Consulta Deducciones."],
            },
          ],
          actions: [
            "Si entregas en departamentos, pide prestado o compra un carrito y prueba un recorrido con él.",
            "Pon un kit de invierno en el vehículo antes de la primera helada.",
            "Prueba tus guantes con la app. Tendrás que tocar, deslizar y tomar fotos.",
          ],
        },
      },
    },
  ],
};
